import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { AssetDto, AssetVersionDto } from '@nusign/picflow-backend-types';
import { assetSideMenuSize } from '../../../consts';
import { arraysEqual } from '../../../lib/arrays-equal';
import { devicePixelRatio } from '../../../lib/device-pixel-ratio';
import { lerp } from '../../../lib/lerp-array';
import { loadImg } from '../../../lib/load-img';
import { loadScript } from '../../../lib/load-script';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';

import type * as PIXI from 'pixi.js';
import { getAssetDimentions } from '../../../gallery/lib/get-asset-dimentions';
import { canDisplayAsset } from '../../../lib/can-display-asset';

declare global {
  interface Window {
    PIXI: typeof PIXI;
  }
}
@Component({
  selector: 'app-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingIndicatorComponent],
  providers: [AssetVersionUrlPipe],
})
export class ImageViewerComponent implements OnChanges, OnDestroy {
  private versionUrlPipe = inject(AssetVersionUrlPipe);
  private zone = inject(NgZone);
  private platformId = inject<string>(PLATFORM_ID);
  @Input() asset: AssetDto;
  @Input() versionData: AssetVersionDto;
  @Input() comparisonVersion?: AssetVersionDto;
  @Input() comparisonPosition?: number;
  @Input() offsetX: number;
  @Input() offsetY: number;
  @Input() scale: number;
  @Input() offsetScale: number;
  @Input() noAnimation: boolean;
  @Input() sideMenuOpen: boolean;
  @Input() isDesktop: boolean;
  @Input() showWebGl: boolean;
  @Output() onImageLoaded = new EventEmitter();
  enabled = false;
  @ViewChild('canvasEl') canvasEl: ElementRef;
  @HostListener('window:resize', ['$event']) async winResize() {
    if (this.enabled) this.handleChanges();
  }

  textures: Map<string, PIXI.Texture<PIXI.Resource>> = new Map();
  currentVals: number[];
  startVals: number[];
  targetVals: number[] = [];
  transitionStartTime: number;
  destroyed = false;
  loadingTextureKeys: Map<string, Promise<PIXI.Texture<PIXI.Resource>>> = new Map();
  density: number;
  pixiRenderer: PIXI.IRenderer<PIXI.ICanvas>;
  pixiStage: PIXI.Container;
  sprite: PIXI.Sprite;
  backgroundSprite: PIXI.TilingSprite;
  comparisonSprite?: PIXI.Sprite;
  comparisonMask: PIXI.Graphics;
  loaded = false;
  loading = signal(false);

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    this.density = devicePixelRatio();
    this.render();
  }

  public get version() {
    return this.versionData || this.asset?.version_data;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.showWebGl && !this.enabled) {
      this.enabled = true;
      this.pixiAppSetup();
    }
    if (this.enabled) this.handleChanges();
    if (!this.showWebGl) this.enabled = false;
    if (changes.image && this.loaded) {
      this.textures.clear();
      this.loadingTextureKeys.clear();
    }
    if (this.loaded) this.loadTextures();

    if (this.comparisonVersion && !this.comparisonSprite && this.sprite) {
      this.setupComparisonSprite();
    }
    if (!this.comparisonVersion && this.comparisonSprite) {
      this.pixiStage.removeChild(this.comparisonSprite);
      delete this.comparisonSprite;
    }

    if (this.comparisonMask) {
      this.comparisonMask.clear();
      const w = (window.innerWidth / 100) * this.comparisonPosition;
      this.comparisonMask.beginFill(0x000000);
      this.comparisonMask.drawRect(w, 0, window.innerWidth - w, window.innerHeight);
    }
  }

  private async getImageUrl(version: AssetVersionDto) {
    const { width } = getAssetDimentions(version, this.getDataBackup());

    return this.versionUrlPipe.transform(
      version,
      version.uuid,
      version['status'],
      version.watermark_uuid,
      width * this.offsetScale * this.scale,
      undefined,
      this.asset.watermark_enabled,
      false,
      true,
      true
    );
  }

  getDataBackup() {
    return { width: 250 * devicePixelRatio(), height: 250 * devicePixelRatio() };
  }

  private async pixiAppSetup() {
    if (this.pixiRenderer) return;
    await loadScript('/pixi.min.js');
    this.zone.runOutsideAngular(() => {
      this.pixiRenderer = window.PIXI.autoDetectRenderer({
        view: this.canvasEl.nativeElement,
        resolution: this.density,
        backgroundAlpha: 0,
        hello: false,
      });
      this.pixiStage = new window.PIXI.Container();
    });
    const textures = await this.getTextures();

    const tilingSpriteTexture = window.PIXI.Texture.from('/assets/png-tile-10-percent.png');
    if (this.destroyed || !this.pixiRenderer) return;

    this.backgroundSprite = new window.PIXI.TilingSprite(tilingSpriteTexture, window.innerWidth, window.innerHeight);
    this.backgroundSprite.zIndex = 0;
    this.backgroundSprite.tileScale.set(0.25);

    this.pixiStage.addChild(this.backgroundSprite);

    this.sprite = new window.PIXI.Sprite(textures[0]);
    this.sprite.zIndex = 2;
    this.pixiStage.addChild(this.sprite);
    this.setSpriteParams(this.sprite);
    this.setBackgroundSpriteParams(this.backgroundSprite);

    if (textures[1]) this.setupComparisonSprite();

    this.resizeCanvas();
    this.loaded = true;
  }

  private async setupComparisonSprite() {
    if (this.comparisonSprite) return;
    const textures = await this.getTextures();
    if (this.comparisonSprite) return;
    this.comparisonMask = new window.PIXI.Graphics();
    this.comparisonSprite = new window.PIXI.Sprite(textures[1]);
    this.pixiStage.addChild(this.comparisonSprite);
    this.setSpriteParams(this.comparisonSprite);
    this.comparisonMask.beginFill(0x000000);

    const w = (window.innerWidth / 100) * this.comparisonPosition;
    this.comparisonMask.drawRect(0, 0, w, window.innerHeight);
    this.comparisonSprite.mask = this.comparisonMask;
  }

  private async getTextures() {
    return Promise.all([this.getTexture(this.version), this.getTexture(this.comparisonVersion)]);
  }

  private async getTexture(version: AssetVersionDto) {
    if (!version) return;
    if (!canDisplayAsset(this.asset, version)) return;
    const url = await this.getImageUrl(version);
    let key = version.data?.width ? url + '?for-canvas=true' : url;
    if (version.data?.width && this.offsetScale * this.scale > 1) key += '#nearest';

    if (this.loadingTextureKeys.has(key)) return this.loadingTextureKeys.get(key);
    if (this.textures.has(key)) {
      this.onImageLoaded.emit();
      return this.textures.get(key);
    }
    let _resolve: (value: PIXI.Texture<PIXI.Resource> | PromiseLike<PIXI.Texture<PIXI.Resource>>) => void;
    const promise: Promise<PIXI.Texture<PIXI.Resource>> = new Promise(resolve => (_resolve = resolve));
    this.loadingTextureKeys.set(key, promise);
    this.loading.set(true);
    let texture: PIXI.Texture<PIXI.Resource>;

    try {
      const image = await loadImg(key);
      texture = window.PIXI.Texture.from(image);
    } catch {}
    this.textures.set(key, texture);
    _resolve(texture);
    this.loading.set(false);
    this.loadingTextureKeys.delete(key);
    this.onImageLoaded.emit();
    if (texture) {
      if (this.offsetScale * this.scale > 1) {
        texture.baseTexture.scaleMode = window.PIXI.SCALE_MODES.NEAREST;
      } else {
        texture.baseTexture.scaleMode = window.PIXI.SCALE_MODES.LINEAR;
      }
    }
    return texture;
  }

  private async loadTextures() {
    const textures = await this.getTextures();
    if (this.sprite.texture !== textures[0]) {
      this.sprite.texture = textures[0];
      this.setSpriteParams(this.sprite);
    }
    if (this.comparisonSprite && this.comparisonSprite.texture !== textures[1]) {
      this.comparisonSprite.texture = textures[1];
      this.setSpriteParams(this.comparisonSprite);
    }
  }

  handleChanges() {
    const size = this.getContainerSize();
    const scale = this.offsetScale * this.scale;
    const width = size.width * this.density;
    const height = size.height * this.density;

    const data = this.version.data?.width ? this.version.data : this.getDataBackup();

    this.targetVals = [0, 0, data.width * scale * this.density, data.height * scale * this.density];

    this.targetVals[0] += width / 2;
    this.targetVals[0] -= this.targetVals[2] / 2;
    this.targetVals[1] += height / 2;
    this.targetVals[1] -= this.targetVals[3] / 2;

    this.targetVals[0] += this.offsetX * this.scale * this.density;
    this.targetVals[1] += this.offsetY * this.scale * this.density;

    if (!this.currentVals) this.currentVals = this.targetVals.slice();

    const animate = !this.noAnimation && !arraysEqual(this.currentVals, this.targetVals);

    if (animate && !this.transitionStartTime) {
      this.transitionStartTime = Date.now();
      this.startVals = this.currentVals.slice();
    }
    if (this.noAnimation) this.transitionStartTime = undefined;
  }

  async render() {
    if (this.destroyed) return;
    if (!this.enabled || !this.loaded) return window.requestAnimationFrame(() => this.render());

    if (this.transitionStartTime) {
      const progress = (1 / 200) * (Date.now() - this.transitionStartTime);
      this.currentVals = lerp(this.startVals, this.targetVals, progress);
      if (progress >= 1) this.transitionStartTime = undefined;
    } else {
      this.currentVals = this.targetVals.slice();
    }

    this.resizeCanvas();
    this.setSpriteParams(this.sprite);
    this.setBackgroundSpriteParams(this.backgroundSprite);
    if (this.comparisonSprite) this.setSpriteParams(this.comparisonSprite, this.sprite);
    window.requestAnimationFrame(() => this.render());
    this.pixiRenderer.render(this.pixiStage);
  }

  private setSpriteParams(sprite: PIXI.Sprite, offset?: PIXI.Sprite) {
    const x = this.currentVals[0] / this.density;
    let y = this.currentVals[1] / this.density;

    if (offset) {
      const aspectDiff = sprite.texture.width / sprite.texture.height - offset.texture.width / offset.texture.height;
      const data = getAssetDimentions(this.versionData, this.getDataBackup());
      y += (aspectDiff * this.scale * this.offsetScale * data.width) / 2;
    }

    const scale = this.currentVals[2] / this.density / sprite.texture.width;

    sprite.x = x;
    sprite.y = y;
    sprite.scale.set(scale);
  }

  private setBackgroundSpriteParams(sprite: PIXI.TilingSprite) {
    const x = this.currentVals[0] / this.density;
    const y = this.currentVals[1] / this.density;

    sprite.x = x;
    sprite.y = y;
    sprite.width = this.currentVals[2] / this.density;
    sprite.height = this.currentVals[3] / this.density;
  }

  private resizeCanvas() {
    if (!this.pixiRenderer) return;
    const size = this.getContainerSize();
    if (this.pixiRenderer.width !== size.width || this.pixiRenderer.height !== size.height) {
      this.pixiRenderer.resize(size.width, size.height);
    }
  }

  private getContainerSize() {
    if (!this.isDesktop) {
      return {
        width: window.innerWidth,
        height: window.innerHeight * (this.sideMenuOpen ? 0.3 : 1),
      };
    }
    return {
      width: window.innerWidth - (this.sideMenuOpen ? assetSideMenuSize : 0),
      height: window.innerHeight,
    };

    // this.canvasEl.nativeElement.width =
    //   (window.innerWidth - (this.sideMenuOpen && !this.isMobile ? assetSideMenuSize : 0)) * devicePixelRatio();
    // this.canvasEl.nativeElement.height =
    //   window.innerHeight * devicePixelRatio() * (this.isMobile && this.sideMenuOpen ? 0.3 : 1);
  }

  ngOnDestroy() {
    this.pixiRenderer?.destroy();
    delete this.pixiRenderer;
    this.destroyed = true;
  }
}
